import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { paceLabel } from "./pace";

const RED = [255, 59, 48];
const DARK = [20, 20, 20];
const MUTED = [163, 163, 163];

let cachedLogo = null;
async function loadLogo() {
  if (cachedLogo) return cachedLogo;
  try {
    const res = await fetch("/athlon-logo.png");
    const blob = await res.blob();
    const dataUrl = await new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });
    cachedLogo = dataUrl;
    return dataUrl;
  } catch {
    return null;
  }
}

function header(doc, title, subtitle, logoDataUrl) {
  doc.setFillColor(...DARK);
  doc.rect(0, 0, doc.internal.pageSize.getWidth(), 30, "F");
  if (logoDataUrl) {
    try {
      doc.addImage(logoDataUrl, "PNG", 14, 4, 22, 22);
    } catch {}
  }
  doc.setTextColor(255, 255, 255);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(16);
  doc.text("ATHLON SPORTS", logoDataUrl ? 40 : 14, 14);
  doc.setFontSize(8);
  doc.setTextColor(...MUTED);
  doc.text("TRAINING CONTROL", logoDataUrl ? 40 : 14, 20);
  doc.setTextColor(...RED);
  doc.setFontSize(11);
  doc.text(title.toUpperCase(), doc.internal.pageSize.getWidth() - 14, 14, { align: "right" });
  if (subtitle) {
    doc.setTextColor(...MUTED);
    doc.setFontSize(9);
    doc.text(subtitle, doc.internal.pageSize.getWidth() - 14, 20, { align: "right" });
  }
}

function footer(doc) {
  const pages = doc.getNumberOfPages();
  for (let i = 1; i <= pages; i++) {
    doc.setPage(i);
    doc.setFontSize(8);
    doc.setTextColor(...MUTED);
    doc.text(
      `Athlon Sports · Generado ${new Date().toLocaleDateString("es-AR")} · ${i}/${pages}`,
      doc.internal.pageSize.getWidth() / 2,
      doc.internal.pageSize.getHeight() - 8,
      { align: "center" }
    );
  }
}

export async function exportRoutinePDF(routine, user) {
  const logo = await loadLogo();
  const doc = new jsPDF();
  header(doc, "Rutina", routine.date || "", logo);

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text(routine.name || "Rutina", 14, 44);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Atleta: ${user?.name || "-"}`, 14, 52);
  doc.text(`Fecha: ${routine.date || "-"}`, 14, 58);

  let y = 70;
  if (routine.objectives) {
    doc.setTextColor(...RED);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(10);
    doc.text("OBJETIVOS DEL DÍA", 14, y);
    y += 6;
    doc.setTextColor(0, 0, 0);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(11);
    const lines = doc.splitTextToSize(routine.objectives, 180);
    doc.text(lines, 14, y);
    y += lines.length * 5 + 6;
  }

  autoTable(doc, {
    startY: y,
    head: [["#", "Ejercicio", "Series", "Reps", "Peso (kg)", "Distancia (m)", "Tiempo (min)", "Ritmo", "Micro (s)", "Macro (s)"]],
    body: (routine.exercises || []).map((ex, i) => [
      i + 1,
      ex.exercise_name || "-",
      ex.sets ?? "-",
      ex.reps ?? "-",
      ex.weight_kg ?? "-",
      ex.distance_m ? ex.distance_m : "-",
      ex.duration_min ? ex.duration_min : "-",
      paceLabel(ex.distance_m, ex.duration_min) || "-",
      ex.micro_pause_sec ?? "-",
      ex.macro_pause_sec ?? "-",
    ]),
    theme: "grid",
    headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 7 },
    bodyStyles: { fontSize: 8 },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    styles: { cellPadding: 2 },
  });

  footer(doc);
  doc.save(`rutina-${(routine.name || "rutina").replace(/\s+/g, "_")}-${routine.date || ""}.pdf`);
}

export async function exportNutritionPDF({ plan, meals, date, user }) {
  const logo = await loadLogo();
  const doc = new jsPDF();
  header(doc, "Plan Nutricional", date || "", logo);

  doc.setTextColor(0, 0, 0);
  doc.setFont("helvetica", "bold");
  doc.setFontSize(22);
  doc.text("Plan Nutricional", 14, 44);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(10);
  doc.setTextColor(...MUTED);
  doc.text(`Atleta: ${user?.name || "-"}`, 14, 52);
  doc.text(`Fecha: ${date || "-"}`, 14, 58);

  // Objetivos
  autoTable(doc, {
    startY: 68,
    head: [["Objetivo diario", "Calorías", "Hidratos (g)", "Proteínas (g)", "Grasas (g)"]],
    body: [[
      "Meta",
      plan.daily_calories ?? 0,
      plan.daily_carbs_g ?? 0,
      plan.daily_protein_g ?? 0,
      plan.daily_fat_g ?? 0,
    ]],
    theme: "grid",
    headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 10 },
  });

  const totals = (meals || []).reduce(
    (a, m) => ({
      calories: a.calories + (m.calories || 0),
      carbs: a.carbs + (m.carbs_g || 0),
      protein: a.protein + (m.protein_g || 0),
      fat: a.fat + (m.fat_g || 0),
    }),
    { calories: 0, carbs: 0, protein: 0, fat: 0 }
  );

  // Comidas
  autoTable(doc, {
    head: [["Comida", "kcal", "Hidratos", "Proteínas", "Grasas"]],
    body: [
      ...(meals || []).map((m) => [
        m.meal_name || "-",
        (m.calories || 0).toFixed(0),
        (m.carbs_g || 0).toFixed(0),
        (m.protein_g || 0).toFixed(0),
        (m.fat_g || 0).toFixed(0),
      ]),
      [
        { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totals.calories.toFixed(0), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totals.carbs.toFixed(0), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totals.protein.toFixed(0), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
        { content: totals.fat.toFixed(0), styles: { fontStyle: "bold", fillColor: [240, 240, 240] } },
      ],
    ],
    theme: "grid",
    headStyles: { fillColor: RED, textColor: 255, fontStyle: "bold", fontSize: 9 },
    bodyStyles: { fontSize: 10 },
  });

  footer(doc);
  doc.save(`nutricion-${(user?.name || "atleta").replace(/\s+/g, "_")}-${date || ""}.pdf`);
}
